import { create, type StateCreator } from 'zustand';
import type { User } from '../services/api';
import { api, authApi } from '../services/api';

/**
 * Backend notification record. Shape intentionally loose — different call
 * sites project different subsets. Consumers map to their own view type.
 */
export interface RawNotification {
  id: string;
  type?: string;
  title?: string;
  message?: string;
  timestamp?: string;
  checklist_id?: string;
  checklist_name?: string;
  is_read?: boolean;
  action?: string;
  [key: string]: unknown;
}

export interface ToastNotification {
  id: string;
  title: string;
  description?: string;
  type?: string;
  checklistId?: string;
  checklistName?: string;
  action?: string;
}

// True when an HttpOnly session cookie is likely active. Role and id are
// never stored in sessionStorage — always fetched from /auth/me on load.
export function hasActiveSession(): boolean {
  if (typeof window === 'undefined') return false;
  return sessionStorage.getItem('session_active') === 'true';
}

interface AppState {
  // Auth
  user: User | null;
  login: (user: User) => void;
  setUser: (user: User | null) => void;
  logout: () => Promise<void>;

  // Notifications
  notifications: RawNotification[];
  setNotifications: (items: RawNotification[]) => void;
  unreadCount: () => number;

  // Toast
  showToast: boolean;
  toastNotification: ToastNotification | null;
  presentToast: (n: ToastNotification) => void;
  dismissToast: () => void;
}

const appStoreCreator: StateCreator<AppState> = (set, get) => ({
  // User starts null — App.tsx populates it via /auth/me when session_active is set.
  user: null,
  notifications: [],
  showToast: false,
  toastNotification: null,

  login: (user: User) => {
    api.userId = user.id;
    // Store only the session marker — role and id stay server-side only.
    sessionStorage.setItem('session_active', 'true');
    set({ user });
  },

  setUser: (user: User | null) => {
    if (user) {
      api.userId = user.id;
      sessionStorage.setItem('session_active', 'true');
    } else {
      api.userId = null;
      sessionStorage.removeItem('session_active');
    }
    set({ user });
  },

  logout: async () => {
    await authApi.logout().catch(() => {});
    api.userId = null;
    sessionStorage.removeItem('session_active');
    set({ user: null, notifications: [], showToast: false, toastNotification: null });
  },

  setNotifications: (items: RawNotification[]) => set({ notifications: items }),

  unreadCount: () => get().notifications.filter((n) => !n.is_read).length,

  presentToast: (n: ToastNotification) => set({ toastNotification: n, showToast: true }),
  dismissToast: () => set({ showToast: false, toastNotification: null }),
});

export const useAppStore = create<AppState>()(appStoreCreator);
